
import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { User } from "lucide-react";
import { CalendarSyncSettings, UserProfileData, countryToTimezone } from "@/types/dashboard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { COUNTRIES } from "@/constants/countries";

const AVAILABLE_AVATARS = [
  'm1.png',
  'm2.png',
  'm3.png',
  'f1.png',
  'f2.png',
  'f3.png',
];

const profileFormSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  phone: z.string().optional(),
  country: z.string().optional(),
  contactSyncEnabled: z.boolean().default(false),
  calendarSync: z.boolean().default(false),
  syncBirthdays: z.boolean().default(true),
  syncAnniversaries: z.boolean().default(true),
  syncCustomEvents: z.boolean().default(false),
  timezone: z.string().optional(),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

interface ProfileFormProps {
  initialData?: UserProfileData;
  onSave: (data: UserProfileData) => void;
  selectedAvatar?: string;
  onAvatarSelect?: (avatar: string) => void;
}

const ProfileForm = ({ initialData, onSave, selectedAvatar, onAvatarSelect }: ProfileFormProps) => {
  const [photoUrl, setPhotoUrl] = useState<string | null>(initialData?.photoUrl || null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [newEmail, setNewEmail] = useState(initialData?.email || "");
  const { toast } = useToast();

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      name: initialData?.name || "",
      email: initialData?.email || "",
      phone: initialData?.phoneNumber || "",
      country: initialData?.country || "",
      contactSyncEnabled: initialData?.contactSyncEnabled || false,
      calendarSync: initialData?.calendarSyncSettings?.enabled || false,
      syncBirthdays: initialData?.calendarSyncSettings?.syncBirthdays || true,
      syncAnniversaries: initialData?.calendarSyncSettings?.syncAnniversaries || true,
      syncCustomEvents: initialData?.calendarSyncSettings?.syncCustomEvents || false,
      timezone: initialData?.timezone || "",
    },
  });

  // Photo upload handler
  const handleImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Handle country change to update timezone
  const handleCountryChange = (country: string) => {
    form.setValue("country", country);
    
    // Update timezone based on country selection
    const newTimezone = countryToTimezone[country] || "";
    if (newTimezone) {
      form.setValue("timezone", newTimezone);
    }
  };

  // Handle email change
  const handleEmailChange = () => {
    if (!newEmail || !newEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }
    
    form.setValue("email", newEmail);
    setShowEmailDialog(false);
    
    toast({
      title: "Email Updated",
      description: "Your email address has been updated.",
    });
  };

  const onSubmit = (values: ProfileFormValues) => {
    const calendarSyncSettings: CalendarSyncSettings = {
      enabled: values.calendarSync,
      syncBirthdays: values.syncBirthdays,
      syncAnniversaries: values.syncAnniversaries,
      syncCustomEvents: values.syncCustomEvents,
    };

    const profileData: UserProfileData = {
      name: values.name,
      email: values.email,
      phoneNumber: values.phone,
      photoUrl: photoUrl || undefined,
      country: values.country,
      timezone: values.timezone,
      contactSyncEnabled: values.contactSyncEnabled,
      calendarSyncSettings: calendarSyncSettings,
      onboardingCompleted: true,
    };

    onSave(profileData);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-6">
          <div>
            <h3 className="text-[20px] font-bold font-euclid text-wabisabi-plum mb-4">Personal Information</h3>

            <div className="space-y-4">
              {/* Avatar Selector */}
              {onAvatarSelect && (
                <div className="space-y-2 mb-8">
                  <Label className="text-[14px] font-medium text-black">Profile Avatar</Label>
                  <div className="flex flex-wrap gap-3">
                    {AVAILABLE_AVATARS.map((avatar) => (
                      <button
                        key={avatar}
                        type="button"
                        onClick={() => onAvatarSelect(avatar)}
                        className={`relative rounded-full transition-all ${
                          selectedAvatar === avatar || selectedAvatar === `/images/avatars/${avatar}`
                            ? 'ring-4 ring-wabisabi-plum ring-offset-2'
                            : 'hover:ring-2 hover:ring-gray-300 hover:ring-offset-2'
                        }`}
                      >
                        <Avatar className="w-16 h-16">
                          <AvatarImage src={`/images/avatars/${avatar}`} alt={avatar} />
                          <AvatarFallback className="bg-gray-200">
                            <User className="w-8 h-8 text-gray-500" />
                          </AvatarFallback>
                        </Avatar>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[14px] font-medium text-black">Full Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Your name"
                        {...field}
                        className="border-gray-300"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[14px] font-medium text-black">Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="your@email.com"
                        {...field}
                        readOnly
                        className="border-gray-300 bg-gray-50"
                      />
                    </FormControl>
                    <FormDescription className="text-[12px]">
                      Contact support to change your email address
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[14px] font-medium text-black">Phone Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="+1 (555) 123-4567"
                        {...field}
                        className="border-gray-300"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[14px] font-medium text-black">Country</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={handleCountryChange}
                    >
                      <FormControl>
                        <SelectTrigger className="border-gray-300">
                          <SelectValue placeholder="Select your country" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="max-h-[300px]">
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        <div className="pt-6 border-t border-gray-200">
          <Button
            type="submit"
            className="bg-[#F4978E] hover:bg-[#F4978E]/90 text-white px-8 py-2 text-[16px]"
          >
            Save Changes
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default ProfileForm;
